import Papa from 'papaparse';

import type { BulkClaimRowResult, ClaimCsvRow } from '~/types/loyaltyContract';

export interface CampaignAction {
  type?: string;
  targetTier?: string;
  bonusPoints?: number;
  statusCredits?: number;
  bonusStatusCredits?: number;
  activityDescription?: string;
  remarks?: string;
  [key: string]: unknown;
}

export function formatAction(action: CampaignAction): string {
  switch (action.type) {
    case 'TIER_UPGRADE':
      return `${
        action.targetTier ? action.targetTier.charAt(0).toUpperCase() + action.targetTier.slice(1) : ''
      } Tier Upgrade`;
    case 'BONUS_POINTS':
      return `${action.bonusPoints ?? ''} Bonus Points`;
    case 'STATUS_CREDITS':
      return `${action.bonusStatusCredits ?? action.statusCredits ?? ''} Status Credits`;
    default:
      return action.type ?? 'Unknown';
  }
}

export function formatCampaignActions(actions: Array<{ [key: string]: unknown }>): string {
  return actions.map((a) => formatAction(a as CampaignAction)).join(', ');
}

const CSV_HEADERS = ['customer_id', 'code'];

interface ParsedCsvRecord {
  customer_id?: string;
  code?: string;
}

interface CsvValidationResult {
  rows: Array<ClaimCsvRow>;
  errors: Array<string>;
}

export function downloadClaimsTemplate(): void {
  const csvContent = Papa.unparse({ fields: CSV_HEADERS, data: [] });
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'claims_template.csv';
  link.click();
  URL.revokeObjectURL(link.href);
}

export function downloadFailedClaimsCsv(failures: Array<BulkClaimRowResult>): void {
  const headers = ['customer_id', 'error'];
  const rows = failures.map((f) => ({
    customer_id: f.customerId,
    error: f.error ?? 'Unknown error',
  }));

  const csvContent = Papa.unparse(rows, { columns: headers });
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `claim_failures_${Date.now()}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

export function parseClaimsCsv(fileContent: string): { data: Array<ParsedCsvRecord>; parseErrors: Array<string> } {
  const { data, errors } = Papa.parse<ParsedCsvRecord>(fileContent, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.toLowerCase().trim(),
  });

  const parseErrors = errors.map((err) => `Row ${err.row}: ${err.message}`);
  return { data, parseErrors };
}

export function validateClaimsCsvRows(data: Array<ParsedCsvRecord>): CsvValidationResult {
  const errors: Array<string> = [];
  const rows: Array<ClaimCsvRow> = [];
  const seenCustomerIds = new Set<string>();

  if (data.length === 0) {
    errors.push('CSV file is empty');
    return { rows, errors };
  }

  data.forEach((record, index) => {
    const rowNum = index + 2; // +2 for header row + 0-index
    const customerId = record.customer_id?.trim();
    const code = record.code?.trim();

    if (!customerId) {
      errors.push(`Row ${rowNum}: missing customer_id`);
      return;
    }

    if (seenCustomerIds.has(customerId)) {
      errors.push(`Row ${rowNum}: duplicate customer_id "${customerId}"`);
      return;
    }

    seenCustomerIds.add(customerId);
    rows.push({ customerId, ...(code ? { code } : {}) });
  });

  return { rows, errors };
}
